import React, { useState, useEffect } from 'react';
import './OneClickNeoTrainer.css';

const OneClickNeoTrainer = () => {
    const [neoStatus, setNeoStatus] = useState('none'); // 'none', 'training', 'ready'
    const [trainingProgress, setTrainingProgress] = useState(0);
    const [bountyReadiness, setBountyReadiness] = useState(null);

    useEffect(() => {
        checkNeoStatus();
    }, []);

    const checkNeoStatus = async () => {
        try {
            const response = await fetch('/api/student/neo/status', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setNeoStatus(data.neo_status?.has_model ? 'ready' : 'none');
                setBountyReadiness(data.bounty_readiness);
            }
        } catch (error) {
            console.error('Error checking Neo status:', error);
        }
    };

    const trainNeoOneClick = async () => {
        setNeoStatus('training');
        setTrainingProgress(0);

        try {
            // Start progress animation
            const progressInterval = setInterval(() => {
                setTrainingProgress(prev => {
                    if (prev >= 95) {
                        clearInterval(progressInterval);
                        return 95;
                    }
                    return prev + Math.random() * 10;
                });
            }, 200);

            const response = await fetch('/api/student/neo/train', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    target_subjects: [], // Smart defaults
                    bounty_focus: true,  // Always optimize for bounties
                    quick_mode: true     // Always use quick mode
                })
            });

            clearInterval(progressInterval);
            setTrainingProgress(100);

            if (response.ok) {
                const result = await response.json();
                
                setTimeout(() => {
                    setNeoStatus('ready');
                    setBountyReadiness(result.training_results?.bounty_readiness);
                    
                    // Show success celebration
                    showCelebration();
                }, 500);
            } else {
                setNeoStatus('none');
                setTrainingProgress(0);
                alert('Training failed. Please try again.');
            }
        } catch (error) {
            console.error('Error training Neo:', error);
            setNeoStatus('none');
            setTrainingProgress(0);
            alert('Training failed. Please try again.');
        }
    };

    const showCelebration = () => {
        // Simple celebration animation
        const celebration = document.createElement('div');
        celebration.className = 'celebration-popup';
        celebration.innerHTML = `
            <div class="celebration-content">
                <div class="celebration-emoji">🎉</div>
                <h3>Your AI Tutor is Ready!</h3>
                <p>Time to crush some bounties!</p>
            </div>
        `;
        document.body.appendChild(celebration);

        setTimeout(() => {
            document.body.removeChild(celebration);
        }, 3000);
    };

    const NoneState = () => (
        <div className="neo-trainer-card">
            <div className="neo-avatar inactive">
                <div className="avatar-circle">
                    <span className="avatar-icon">🤖</span>
                </div>
                <div className="avatar-status">No AI Tutor</div>
            </div>

            <div className="trainer-content">
                <h2>Get Your Personal AI Tutor</h2>
                <p>Train your own AI tutor in 2 minutes. It will help you:</p>
                
                <div className="benefits-list">
                    <div className="benefit">
                        <span className="benefit-icon">💰</span>
                        <span>Earn more on bounties</span>
                    </div>
                    <div className="benefit">
                        <span className="benefit-icon">🎯</span>
                        <span>Get personalized help</span>
                    </div>
                    <div className="benefit">
                        <span className="benefit-icon">⚡</span>
                        <span>Learn faster</span>
                    </div>
                </div>

                <button 
                    className="train-button"
                    onClick={trainNeoOneClick}
                >
                    <span className="button-icon">✨</span>
                    Train My AI Tutor
                    <span className="button-time">(~2 minutes)</span>
                </button>

                <div className="social-proof">
                    <span className="proof-text">Join 12,847 students with personal AI tutors</span>
                </div>
            </div>
        </div>
    );

    const TrainingState = () => (
        <div className="neo-trainer-card training">
            <div className="neo-avatar training">
                <div className="avatar-circle">
                    <span className="avatar-icon spinning">🤖</span>
                </div>
                <div className="avatar-status">Training...</div>
            </div>

            <div className="trainer-content">
                <h2>Training Your AI Tutor</h2>
                <p>Learning your style and optimizing for bounty success...</p>
                
                <div className="progress-container">
                    <div className="progress-bar">
                        <div 
                            className="progress-fill"
                            style={{ width: `${trainingProgress}%` }}
                        ></div>
                    </div>
                    <div className="progress-text">{Math.round(trainingProgress)}% complete</div>
                </div>

                <div className="training-steps">
                    <div className={`step ${trainingProgress > 20 ? 'complete' : 'active'}`}>
                        <span className="step-icon">📚</span>
                        <span>Analyzing your learning history</span>
                    </div>
                    <div className={`step ${trainingProgress > 60 ? 'complete' : trainingProgress > 20 ? 'active' : ''}`}>
                        <span className="step-icon">🎯</span>
                        <span>Optimizing for bounty performance</span>
                    </div>
                    <div className={`step ${trainingProgress > 90 ? 'complete' : trainingProgress > 60 ? 'active' : ''}`}>
                        <span className="step-icon">⚡</span>
                        <span>Personalizing responses</span>
                    </div>
                </div>
            </div>
        </div>
    );

    const ReadyState = () => (
        <div className="neo-trainer-card ready">
            <div className="neo-avatar ready">
                <div className="avatar-circle">
                    <span className="avatar-icon">🤖</span>
                    <div className="ready-pulse"></div>
                </div>
                <div className="avatar-status">Ready to Help!</div>
            </div>

            <div className="trainer-content">
                <h2>Your AI Tutor is Ready! 🎉</h2>
                <p>Your personal AI tutor has been trained and is ready to help you succeed.</p>
                
                {bountyReadiness && (
                    <div className="readiness-overview">
                        <h4>Bounty Readiness</h4>
                        <div className="readiness-levels">
                            <div className={`level ${bountyReadiness.beginner_ready ? 'ready' : 'not-ready'}`}>
                                <span className="level-icon">{bountyReadiness.beginner_ready ? '✅' : '⏳'}</span>
                                <span>Beginner Bounties</span>
                            </div>
                            <div className={`level ${bountyReadiness.intermediate_ready ? 'ready' : 'not-ready'}`}>
                                <span className="level-icon">{bountyReadiness.intermediate_ready ? '✅' : '⏳'}</span>
                                <span>Intermediate Bounties</span>
                            </div>
                            <div className={`level ${bountyReadiness.advanced_ready ? 'ready' : 'not-ready'}`}>
                                <span className="level-icon">{bountyReadiness.advanced_ready ? '✅' : '⏳'}</span>
                                <span>Advanced Bounties</span>
                            </div>
                        </div>
                        <div className="recommended-level">
                            Start with: <strong>{bountyReadiness.recommended_level}</strong> bounties
                        </div>
                    </div>
                )}

                <div className="action-buttons">
                    <button className="primary-action" onClick={() => window.location.href = '/bounties'}>
                        <span className="button-icon">💰</span>
                        Browse Bounties
                    </button>
                    <button className="secondary-action" onClick={() => window.location.href = '/chat'}>
                        <span className="button-icon">💬</span>
                        Chat with Neo
                    </button>
                </div>

                <div className="retrain-section">
                    <button 
                        className="retrain-button"
                        onClick={trainNeoOneClick}
                    >
                        🔄 Retrain for Better Performance
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="one-click-neo-trainer">
            {neoStatus === 'none' && <NoneState />}
            {neoStatus === 'training' && <TrainingState />}
            {neoStatus === 'ready' && <ReadyState />}
        </div>
    );
};

export default OneClickNeoTrainer;